import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 10000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getQuote = async (tickers, options = {}) => {
  const { range = '1d', interval = '1d', fundamental = false, dividends = false } = options;
  const tickerStr = Array.isArray(tickers) ? tickers.join(',') : tickers;
  const response = await api.get(`/api/quote/${tickerStr}`, {
    params: { range, interval, fundamental, dividends },
  });
  return response.data;
};

export const getAtivosDisponiveis = async (search = '') => {
  const response = await api.get('/api/ativos', {
    params: { search },
  });
  return response.data;
};

export const getAtivosList = async (options = {}) => {
  const { search = '', sortBy = 'name', sortOrder = 'asc', limit = 50, page = 1 } = options;
  const response = await api.get('/api/ativos/lista', {
    params: { search, sortBy, sortOrder, limit, page },
  });
  return response.data;
};

export const getInflacao = async (options = {}) => {
  const { country = 'brazil', historical = false, start, end } = options;
  const params = { country, historical };
  if (start) params.start = start;
  if (end) params.end = end;
  const response = await api.get('/api/inflacao', { params });
  return response.data;
};

export const getPrimeRate = async (options = {}) => {
  const { country = 'brazil', historical = false, start, end } = options;
  const params = { country, historical };
  if (start) params.start = start;
  if (end) params.end = end;
  const response = await api.get('/api/prime-rate', { params });
  return response.data;
};

export const getCrypto = async (coins, currency = 'BRL') => {
  const coinStr = Array.isArray(coins) ? coins.join(',') : coins;
  const response = await api.get(`/api/crypto/${coinStr}`, {
    params: { currency },
  });
  return response.data;
};

export const getCambio = async (pairs = 'USD-BRL,EUR-BRL') => {
  const response = await api.get('/api/cambio', {
    params: { currency: pairs },
  });
  return response.data;
};

export const getUniverse = async () => {
  const response = await api.get('/api/universe');
  return response.data;
};

export const getMarketOverview = async () => {
  const response = await api.get('/api/market/overview');
  return response.data;
};

export const getKairi = async (tickers) => {
  const tickerStr = Array.isArray(tickers) ? tickers.join(',') : tickers;
  const response = await api.get(`/api/kairi/${tickerStr}`, { timeout: 45000 });
  return response.data;
};

export const getRSI2 = async (tickers) => {
  const tickerStr = Array.isArray(tickers) ? tickers.join(',') : tickers;
  const response = await api.get(`/api/rsi2/${tickerStr}`, { timeout: 35000 });
  return response.data;
};

export const getStrategyHealth = async () => {
  const response = await api.get('/api/strategies/health', { timeout: 30000 });
  return response.data;
};

export const getSetorial = async () => {
  const response = await api.get('/api/setorial', { timeout: 60000 });
  return response.data;
};

export const getScanner = async () => {
  const response = await api.get('/api/scanner/hoje', { timeout: 15000 });
  return response.data;
};

export const getIntradayScanner = async () => {
  const response = await api.get('/api/scanner/intraday', { timeout: 15000 });
  return response.data;
};

export const getReversalMonitor = async () => {
  const response = await api.get('/api/reversal/hoje', { timeout: 60000 });
  return response.data;
};

export const getReversalHistory = async (ticker, options = {}) => {
  const { range = '5y' } = options;
  const response = await api.get(`/api/reversal/historico/${encodeURIComponent(ticker)}`, {
    params: { range },
    timeout: 45000,
  });
  return response.data;
};

export const getBreakout55Monitor = async ({ tier = 'BLUE' } = {}) => {
  const response = await api.get('/api/breakout55/hoje', {
    params: { tier },
    timeout: 60000,
  });
  return response.data;
};

export const getBacktestEvidence = async (strategy) => {
  const response = await api.get(`/api/backtests/${encodeURIComponent(strategy)}/evidence`, {
    timeout: 30000,
  });
  return response.data;
};

export const getBacktestDiagnostics = async (strategy) => {
  const response = await api.get(`/api/backtests/${encodeURIComponent(strategy)}/diagnostics`, {
    timeout: 30000,
  });
  return response.data;
};

export const getBreakout55History = async (ticker) => {
  const response = await api.get(`/api/breakout55/historico/${encodeURIComponent(ticker)}`, {
    params: { range: '5y' },
    timeout: 45000,
  });
  return response.data;
};

export const checkBackendHealth = async () => {
  const response = await api.get('/api/admin/market-data-health');
  return response.data;
};
